import React, { useState } from 'react';
import { Menu, X, Send, Phone, Mail, ExternalLink, ChevronDown, ArrowRight } from 'lucide-react';
import { Link } from 'react-router-dom';


const Header = ({ 
    isScrolled, 
    onApply, 
    logo,
    colleges = [], 
    applyUrl = '',
    email = 'admissions@aryasabhyata.edu.in',
    phone = '+91 91234 56789'
}) => {
    const displayLogo = "/logo.png";
    const [activeDropdown, setActiveDropdown] = useState(null);
    const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
    const [activeCategory, setActiveCategory] = useState(null);

    const parseSafe = (data) => {
        if (!data) return [];
        if (Array.isArray(data)) return data;
        try {
            return typeof data === 'string' ? JSON.parse(data) : data;
        } catch (e) {
            return [];
        }
    };

    const navLinks = [
        { name: 'Home', href: '/' },
        { name: 'About', href: '/about' },
       
        { name: 'ACADEMIC', key: 'academic', hasDropdown: true },
        { name: 'Student Desk', href: '/student-desk' },
        // { name: 'News & Events', href: '/news-events' },
        { name: 'Notice Board', href: '/notice' },
        { name: 'Gallery', href: '/gallery' },
        { name: 'Blog', href: '/blog' },
        { name: 'Contact', href: '/contact' },
    ];

    const getDropdownData = (key) => {
        switch(key) {
            case 'academic': return parseSafe(colleges);
            default: return [];
        }
    };

    // Helper to ensure URLs are absolute for external redirection
    const formatUrl = (url) => {
        if (!url) return '#';
        if (url.startsWith('http://') || url.startsWith('https://')) return url;
        return `https://${url}`;
    };



    const handleApplyClick = () => {
        if (applyUrl) {
            window.open(formatUrl(applyUrl), '_blank');
        } else {
            onApply();
        }
    };

    return (
        <nav className={`sticky top-0 w-full z-[100] transition-all duration-300 ${isScrolled ? 'bg-white shadow-xl' : 'bg-white'}`}>
            <div className={`max-w-auto mx-auto px-2 sm:px-6 lg:px-8 flex justify-between items-center transition-all duration-300 ${isScrolled ? 'h-16' : 'h-16 sm:h-20'}`}>
                <div className="flex items-center space-x-5">
                    <Link to="/" onClick={() => setMobileMenuOpen(false)}>
                        <img 
                            src={displayLogo} alt="Arya Sabhyata" className="h-[36px] sm:h-[48px] w-auto object-contain"
                            onError={(e) => { e.target.src = "https://placehold.co/240x80/ffffff/cc0000?text=Arya+Sabhyata+Group"; }}
                        />
                    </Link>
                    <div className="hidden xl:block border-l border-slate-200 h-10 sm:h-12 ml-2 pl-5">
                        <img 
                            src="/logo-square.png" alt="Badge" className="h-10 sm:h-12 w-auto shadow-sm"
                            onError={(e) => { e.target.style.display = "none"; }}
                        />
                    </div>
                </div>
                
                {/* Desktop Menu */}
                <div className="hidden lg:flex items-center space-x-1 uppercase text-[12px] font-bold h-full">
                    {navLinks.map((link) => {
                        const dropdownData = link.hasDropdown ? getDropdownData(link.key) : [];
                        const hasDropdownContent = dropdownData.length > 0;

                        return (link.hasDropdown && hasDropdownContent) ? (
                            <div key={link.name} className="group relative h-full flex items-center">
                                {link.href ? (
                                    <Link to={link.href} className="px-4 h-full hover:text-[#cc0000] flex items-center transition-colors">
                                        {link.name} <ChevronDown size={14} className="ml-1 group-hover:rotate-180 transition-transform" />
                                    </Link>
                                ) : (
                                    <button className="px-4 h-full hover:text-[#cc0000] flex items-center transition-colors">
                                        {link.name} <ChevronDown size={14} className="ml-1 group-hover:rotate-180 transition-transform" />
                                    </button>
                                )}
                                
                                {/* MEGA DROPDOWN */}
                                <div 
                                    className="absolute top-full left-1/2 -translate-x-1/2 w-[700px] bg-white shadow-2xl invisible group-hover:visible opacity-0 group-hover:opacity-100 transition-all border-t-4 border-[#cc0000] z-[100] flex mt-0 min-h-[350px]"
                                    onMouseLeave={() => setActiveCategory(null)}
                                >
                                    {/* Left Column - Categories */}
                                    <div className="w-1/3 bg-slate-50 border-r border-slate-100 py-4">
                                        <ul className="flex flex-col w-full">
                                            {dropdownData.map((cat, idx) => {
                                                const isHovered = activeCategory === cat.category;
                                                return (
                                                    <li 
                                                        key={idx}
                                                        onMouseEnter={() => setActiveCategory(cat.category)}
                                                        className={`px-6 py-4 cursor-pointer text-[12px] font-bold tracking-widest flex items-center justify-between transition-colors uppercase ${isHovered ? 'bg-white text-[#cc0000] shadow-sm ml-1 border-l-2 border-[#cc0000]' : 'text-[#003366] hover:bg-white border-l-2 border-transparent hover:text-[#cc0000]'}`}
                                                    >
                                                        <span className="truncate pr-2">{cat.category}</span>
                                                        <ChevronDown size={14} className={`shrink-0 transform -rotate-90 transition-transform ${isHovered ? 'text-[#cc0000]' : 'text-slate-300'}`} />
                                                    </li>
                                                );
                                            })}
                                        </ul>
                                    </div>
                                    
                                    {/* Right Column - Colleges */}
                                    <div className="w-2/3 p-6 bg-white overflow-y-auto max-h-[500px]">
                                         {activeCategory ? (
                                             <div className="animate-fadeIn">
                                                 <h4 className="text-[#003366] text-[12px] font-bold tracking-widest mb-4 border-b border-slate-100 pb-2 uppercase flex items-center">
                                                     {(() => {
                                                         const activeCatData = dropdownData.find(cat => cat.category === activeCategory);
                                                         if (activeCatData?.subCategories?.length === 1 && activeCatData.subCategories[0].name) {
                                                             return activeCatData.subCategories[0].name;
                                                         }
                                                         return activeCategory;
                                                     })()}
                                                 </h4>
                                                 <div className="space-y-6">
                                                     {(() => {
                                                         const activeCatData = dropdownData.find(cat => cat.category === activeCategory);
                                                         const hasMultipleSubs = activeCatData?.subCategories?.length > 1;
                                                         
                                                         // If new structure (subCategories) exists
                                                         if (activeCatData?.subCategories && activeCatData.subCategories.length > 0) {
                                                             return activeCatData.subCategories.map((sub, sIdx) => (
                                                                 <div key={sIdx} className="space-y-2">
                                                                     {sub.name && hasMultipleSubs && (
                                                                         <h5 className="text-[10px] font-black text-[#cc0000] uppercase tracking-widest mb-2 flex items-center">
                                                                             <ArrowRight size={10} className="mr-1" /> {sub.name}
                                                                         </h5>
                                                                     )}
                                                                     <div className="grid grid-cols-1 gap-2">
                                                                         {(sub.items || []).map((item, iIdx) => (
                                                                             <a 
                                                                                 key={iIdx} 
                                                                                 href={formatUrl(item.url)} 
                                                                                 target="_blank" 
                                                                                 rel="noopener noreferrer"
                                                                                 className="flex items-center justify-between p-3 hover:bg-slate-50 border border-transparent hover:border-slate-100 text-slate-700 hover:text-[#cc0000] rounded-sm transition-all normal-case font-semibold text-[13px] group/link shadow-sm"
                                                                             >
                                                                                 <span className="pr-4">{item.name}</span>
                                                                                 <ExternalLink size={14} className="shrink-0 opacity-0 group-hover/link:opacity-100 transition-opacity" />
                                                                             </a>
                                                                         ))}
                                                                     </div>
                                                                 </div>
                                                             ));
                                                         }
                                                         
                                                         // Fallback to old structure (colleges)
                                                         return (
                                                             <div className="space-y-2">
                                                                 {activeCatData?.colleges?.map((item, cIdx) => (
                                                                     <a 
                                                                         key={cIdx} 
                                                                         href={formatUrl(item.url)} 
                                                                         target="_blank" 
                                                                         rel="noopener noreferrer"
                                                                         className="flex items-center justify-between p-3 hover:bg-slate-50 border border-transparent hover:border-slate-100 text-slate-700 hover:text-[#cc0000] rounded-sm transition-all normal-case font-semibold text-[13px] group/link shadow-sm"
                                                                     >
                                                                         <span className="pr-4">{item.name}</span>
                                                                         <ExternalLink size={14} className="shrink-0 opacity-0 group-hover/link:opacity-100 transition-opacity" />
                                                                     </a>
                                                                 ))}
                                                             </div>
                                                         );
                                                     })()}
                                                 </div>
                                             </div>
                                         ) : (
                                             <div className="h-full flex flex-col items-center justify-center text-slate-400">
                                                 <div className="w-16 h-16 bg-slate-50 rounded-full flex items-center justify-center mb-3 text-slate-300">
                                                     <ChevronDown size={24} className="-rotate-90" />
                                                 </div>
                                                 <p className="text-sm font-black uppercase tracking-widest text-[#cc0000]">Welcome to</p>
                                                 <p className="text-[11px] font-bold uppercase tracking-widest text-[#003366] mt-2">Arya Sabhyata Group of Institutions</p>
                                             </div>
                                         )}
                                     </div>
                                </div>
                            </div>
                        ) : (
                            link.href ? (
                                link.href.startsWith('#') ? (
                                    <a key={link.name} href={link.href} className="px-4 py-2 hover:text-[#cc0000] transition-colors whitespace-nowrap">{link.name}</a>
                                ) : (
                                    <Link key={link.name} to={link.href} className="px-4 py-2 hover:text-[#cc0000] transition-colors whitespace-nowrap">{link.name}</Link>
                                )
                            ) : null
                        );
                    })}
                    <button 
                        onClick={handleApplyClick}
                        className="ml-4 bg-[#cc0000] text-white px-5 py-1.5 flex items-center hover:bg-[#003366] transition-colors shadow-lg text-[11px] whitespace-nowrap"
                    >
                        <Send size={12} className="mr-2" /> APPLY NOW
                    </button>
                </div>

                {/* Mobile Menu Button */}
                <button className="lg:hidden text-[#cc0000]" onClick={() => setMobileMenuOpen(!mobileMenuOpen)}>
                    {mobileMenuOpen ? <X size={28} /> : <Menu size={28} />}
                </button>
            </div>

            {/* Mobile Menu Overlay */}
            <div className={`fixed inset-0 top-16 bg-white z-40 lg:hidden transition-transform duration-300 ease-in-out transform ${mobileMenuOpen ? 'translate-x-0' : 'translate-x-full'}`}>
                {/* Close Button inside drawer */}
                <button
                    onClick={() => setMobileMenuOpen(false)}
                    className="absolute top-4 right-4 z-50 bg-[#cc0000] text-white p-2 rounded-full shadow-lg hover:bg-[#003366] transition-colors"
                    aria-label="Close menu"
                >
                    <X size={20} />
                </button>
                <div className="flex flex-col p-6 space-y-4 h-full overflow-y-auto pb-32 focus:outline-none">
                    {navLinks.map((link) => {
                        const dropdownData = link.hasDropdown ? getDropdownData(link.key) : [];
                        const hasDropdownContent = dropdownData.length > 0;

                        return (link.hasDropdown && hasDropdownContent) ? (
                            <div key={link.name} className="border-b border-slate-100 pb-2">
                                <button 
                                    onClick={() => setActiveDropdown(activeDropdown === link.key ? null : link.key)}
                                    className="w-full flex justify-between items-center text-lg font-bold uppercase text-slate-800 py-3 hover:text-[#cc0000]"
                                >
                                    <span>{link.name}</span>
                                    <ChevronDown size={20} className={`transition-transform ${activeDropdown === link.key ? 'rotate-180' : ''}`} />
                                </button>
                                
                                {activeDropdown === link.key && (
                                    <div className="pl-4 space-y-6 mt-4 pb-4">
                                        {link.href && (
                                            <div className="mb-4">
                                                <Link 
                                                    to={link.href} 
                                                    onClick={() => setMobileMenuOpen(false)}
                                                    className="flex items-center text-[15px] font-black text-[#cc0000] uppercase tracking-tighter"
                                                >
                                                    View {link.name} Hub <ArrowRight size={14} className="ml-2" />
                                                </Link>
                                            </div>
                                        )}
                                        {dropdownData.map((cat, idx) => (
                                            <div key={idx}>
                                                <p className="text-[10px] tracking-widest text-slate-400 uppercase font-black mb-3">{cat.category}</p>
                                                <div className="space-y-6">
                                                    {(() => {
                                                        const hasMultipleSubs = cat.subCategories?.length > 1;
                                                        if (cat.subCategories && cat.subCategories.length > 0) {
                                                            return cat.subCategories.map((sub, sIdx) => (
                                                                <div key={sIdx} className="pl-2 border-l-2 border-slate-100">
                                                                    {sub.name && hasMultipleSubs && <p className="text-[9px] font-black text-[#cc0000] uppercase mb-2 italic">{sub.name}</p>}
                                                                    <div className="space-y-3">
                                                                        {(sub.items || []).map((item, iIdx) => (
                                                                            <a 
                                                                                key={iIdx} 
                                                                                href={formatUrl(item.url)} 
                                                                                target="_blank" 
                                                                                rel="noopener noreferrer"
                                                                                className="flex items-center text-[15px] font-bold text-[#003366] hover:text-[#cc0000] transition-colors"
                                                                            >
                                                                                {item.name}
                                                                                <ExternalLink size={12} className="ml-2 opacity-50" />
                                                                            </a>
                                                                        ))}
                                                                    </div>
                                                                </div>
                                                            ));
                                                        }
                                                        
                                                        // Fallback
                                                        return (
                                                            <div className="space-y-3">
                                                                {(cat.colleges || []).map((item, cIdx) => (
                                                                    <a 
                                                                        key={cIdx} 
                                                                        href={formatUrl(item.url)} 
                                                                        target="_blank" 
                                                                        rel="noopener noreferrer"
                                                                        className="flex items-center text-[15px] font-bold text-[#003366] hover:text-[#cc0000] transition-colors"
                                                                    >
                                                                        {item.name}
                                                                        <ExternalLink size={12} className="ml-2 opacity-50" />
                                                                    </a>
                                                                ))}
                                                            </div>
                                                        );
                                                    })()}
                                                </div>
                                            </div>
                                        ))}
                                    </div>
                                )}
                            </div>
                        ) : (
                            link.href ? (
                                link.href.startsWith('#') ? (
                                    <a 
                                        key={link.name} 
                                        href={link.href} 
                                        onClick={() => setMobileMenuOpen(false)}
                                        className="text-lg font-bold uppercase text-slate-800 border-b border-slate-100 pb-3 hover:text-[#cc0000]"
                                    >
                                        {link.name}
                                    </a>
                                ) : (
                                    <Link 
                                        key={link.name} 
                                        to={link.href} 
                                        onClick={() => setMobileMenuOpen(false)}
                                        className="text-lg font-bold uppercase text-slate-800 border-b border-slate-100 pb-3 hover:text-[#cc0000]"
                                    >
                                        {link.name}
                                    </Link>
                                )
                            ) : null
                        );
                    })}
                    <button 
                        onClick={() => {
                            setMobileMenuOpen(false);
                            handleApplyClick();
                        }}
                        className="bg-[#cc0000] text-white px-6 py-4 font-bold uppercase flex justify-center items-center hover:bg-[#003366] transition-colors shadow-lg mt-4"
                    >
                        <Send size={18} className="mr-3" /> APPLY NOW
                    </button>

                    <div className="mt-8 pt-10 border-t border-slate-100 space-y-4">
                        <div className="flex items-center text-slate-600">
                            <Phone size={20} className="mr-3 text-[#cc0000]" />
                            <span className="font-bold">{phone}</span>
                        </div>
                        <div className="flex items-center text-slate-600">
                            <Mail size={20} className="mr-3 text-[#cc0000]" />
                            <span className="font-bold text-sm">{email}</span>
                        </div>
                    </div>
                </div>
            </div>
        </nav>
    );
};

export default Header;



